/**
 * InvoicesCard — supplier invoices against a purchase order (BLU-456).
 *
 * Replaces the ad-hoc "Invoice & Billing" card on PO detail. Multiple
 * invoices per PO (one deposit max), per-invoice Xero bill push, and a
 * totals-first reconciliation strip (invoiced vs PO total).
 *
 * Legacy compatibility: POs that used the old PO-level invoice flow
 * (purchase_orders.invoice_* / deposit_* columns) keep that flow working —
 * the legacy block (file upload, invoice amount, PO-level Xero pushes)
 * renders inside this card until the first po_invoices row exists, at which
 * point the new flow owns the surface.
 */
import { useMemo, useState } from 'react';
import { isBuyListPoKind } from '@threadheads/shared';
import {
  Check,
  Download,
  ExternalLink,
  File,
  FileText,
  Link2,
  Lock,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useRole } from '@/hooks/use-role';
import { reportError } from '@/lib/logging';
import { readFileAsBase64 } from '@/lib/read-file-base64';
import type { PurchaseOrder } from '@/features/purchase-orders';
import { InvoiceModal, type PoInvoice } from './InvoiceModal';

const XERO_BILL_URL = 'https://go.xero.com/AccountsPayable/View.aspx?invoiceID=';

interface InvoicesCardProps {
  order: PurchaseOrder;
  paymentTermsDays: number | null;
}

const formatDateOnly = (value: string | null): string | null => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(date);
};

const formatBytes = (value?: number | null): string => {
  if (!value || value <= 0) return '--';
  const kb = value / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

export function InvoicesCard({ order, paymentTermsDays }: InvoicesCardProps) {
  const utils = trpc.useUtils();
  const { format } = useCurrencyFormatter();
  const { canManagePurchaseOrders } = useRole();
  const currency = order.currency ?? 'AUD';

  const canManage = canManagePurchaseOrders() && order.status !== 'cancelled';
  // BLU-1268: a just-in-time PO is billed from its own page, once, after
  // Complete; it never renders this card, and if it ever did it must not push.
  // An order-and-wait PO is the same kind of buy list.
  const canPushXero =
    order.status !== 'draft' && order.status !== 'cancelled' && !isBuyListPoKind(order.kind);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<PoInvoice | null>(null);
  const [creatingDeposit, setCreatingDeposit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PoInvoice | null>(null);
  const [pushingInvoiceId, setPushingInvoiceId] = useState<string | null>(null);
  const [isUploadingLegacyFile, setIsUploadingLegacyFile] = useState(false);
  const [invoiceAmountInput, setInvoiceAmountInput] = useState<string>(() => {
    const stored = (order as { invoice_amount?: number | null }).invoice_amount;
    return stored !== null && stored !== undefined ? String(stored) : '';
  });

  const invoicesQuery = trpc.poInvoices.list.useQuery({ purchaseOrderId: order.id });
  const summaryQuery = trpc.poInvoices.summary.useQuery({ purchaseOrderId: order.id });
  const filesQuery = trpc.purchaseOrders.listPurchaseOrderFiles.useQuery({ orderId: order.id });

  const invoices = useMemo(() => invoicesQuery.data ?? [], [invoicesQuery.data]);
  const summary = summaryQuery.data;
  const hasInvoices = invoices.length > 0;
  const hasDeposit = invoices.some((invoice) => invoice.is_deposit);

  const fileById = useMemo(() => {
    const map = new Map<string, { id: string; file_name: string; file_size: number | null }>();
    for (const file of filesQuery.data ?? []) {
      map.set(file.id, { id: file.id, file_name: file.file_name, file_size: file.file_size });
    }
    return map;
  }, [filesQuery.data]);

  const invoiceFiles = useMemo(
    () => (filesQuery.data ?? []).filter((file) => file.file_type === 'invoice'),
    [filesQuery.data]
  );

  // Legacy PO-level invoice data (pre multi-invoice). Only surfaced while no
  // po_invoices rows exist — the moment the new flow is adopted, the legacy
  // controls retire (data stays in the DB untouched).
  const legacyInvoiceNumber = (order as { invoice_number?: string | null }).invoice_number ?? null;
  const legacyInvoiceAmount =
    (order as { invoice_amount?: number | null }).invoice_amount ?? null;
  const hasLegacy =
    !hasInvoices &&
    Boolean(
      legacyInvoiceNumber ||
        order.xero_invoice_id ||
        order.deposit_xero_bill_id ||
        legacyInvoiceAmount !== null ||
        invoiceFiles.length > 0
    );

  const invalidateInvoices = async () => {
    await Promise.all([
      utils.poInvoices.list.invalidate({ purchaseOrderId: order.id }),
      utils.poInvoices.summary.invalidate({ purchaseOrderId: order.id }),
      utils.purchaseOrders.listPurchaseOrderFiles.invalidate({ orderId: order.id }),
    ]);
  };

  const pushInvoice = trpc.poInvoices.push.useMutation({
    onSuccess: async (result) => {
      await invalidateInvoices();
      if (result.attachmentWarning) {
        toast({
          title: 'Pushed to Xero with a warning',
          description: result.attachmentWarning,
        });
      } else {
        toast({ title: 'Invoice pushed to Xero' });
      }
    },
    onError: (error) => {
      toast({ title: 'Failed to push to Xero', description: error.message, variant: 'destructive' });
    },
    onSettled: () => setPushingInvoiceId(null),
  });

  const deleteInvoice = trpc.poInvoices.delete.useMutation({
    onSuccess: async () => {
      await invalidateInvoices();
      setDeleteTarget(null);
      toast({ title: 'Invoice deleted' });
    },
    onError: (error) => {
      toast({ title: 'Failed to delete invoice', description: error.message, variant: 'destructive' });
    },
  });

  // ---- Legacy PO-level mutations (relocated from the old card) ----
  const uploadFile = trpc.purchaseOrders.uploadPurchaseOrderFile.useMutation({
    onSuccess: async (result) => {
      await utils.purchaseOrders.listPurchaseOrderFiles.invalidate({ orderId: order.id });
      toast({ title: 'File uploaded', description: result.file_name });
    },
    onError: (error) => {
      toast({ title: 'Failed to upload file', description: error.message, variant: 'destructive' });
    },
    onSettled: () => setIsUploadingLegacyFile(false),
  });

  const deleteFile = trpc.purchaseOrders.deletePurchaseOrderFile.useMutation({
    onSuccess: async () => {
      await utils.purchaseOrders.listPurchaseOrderFiles.invalidate({ orderId: order.id });
      toast({ title: 'File deleted' });
    },
    onError: (error) => {
      toast({ title: 'Failed to delete file', description: error.message, variant: 'destructive' });
    },
  });

  const getDownloadUrl = trpc.purchaseOrders.getPurchaseOrderFileDownloadUrl.useMutation({
    onError: (error) => {
      toast({
        title: 'Failed to get download link',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateInvoiceAmount = trpc.purchaseOrders.updateInvoiceAmount.useMutation({
    onSuccess: () => utils.purchaseOrders.getOrders.invalidate(),
    onError: (error) => {
      toast({
        title: 'Failed to update invoice amount',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const pushLegacyBill = trpc.xero.pushPurchaseOrder.useMutation({
    onSuccess: async () => {
      await utils.purchaseOrders.getOrders.invalidate();
      toast({ title: 'Purchase order pushed to Xero' });
    },
    onError: (error) => {
      toast({ title: 'Failed to push to Xero', description: error.message, variant: 'destructive' });
    },
  });

  const pushLegacyDeposit = trpc.xero.pushPurchaseOrderDeposit.useMutation({
    onSuccess: async () => {
      await utils.purchaseOrders.getOrders.invalidate();
      toast({ title: 'Purchase order deposit pushed to Xero' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to push deposit to Xero',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleDownloadFile = async (fileId: string) => {
    try {
      const result = await getDownloadUrl.mutateAsync({ fileId });
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch {
      // handled by mutation onError
    }
  };

  const handleLegacyFileUpload = async (file: File) => {
    const MAX_FILE_BYTES = 7 * 1024 * 1024;
    if (file.size > MAX_FILE_BYTES) {
      toast({ title: 'File is too large', description: 'Max ~7MB', variant: 'destructive' });
      return;
    }
    setIsUploadingLegacyFile(true);
    try {
      const base64 = await readFileAsBase64(file);
      uploadFile.mutate({
        orderId: order.id,
        fileType: 'invoice',
        fileName: file.name,
        contentType: file.type || null,
        base64,
      });
    } catch (error) {
      setIsUploadingLegacyFile(false);
      reportError('Error reading invoice file for upload', error);
      toast({ title: 'Failed to read file', variant: 'destructive' });
    }
  };

  const handleInvoiceAmountBlur = () => {
    const trimmed = invoiceAmountInput.trim();
    const numValue = trimmed ? Number(trimmed) : null;
    if (numValue !== null && Number.isNaN(numValue)) return;
    if (numValue !== legacyInvoiceAmount) {
      updateInvoiceAmount.mutate({ orderId: order.id, invoiceAmount: numValue });
    }
  };

  const openCreate = (deposit: boolean) => {
    setEditingInvoice(null);
    setCreatingDeposit(deposit);
    setModalOpen(true);
  };

  const openEdit = (invoice: PoInvoice) => {
    setEditingInvoice(invoice);
    setCreatingDeposit(false);
    setModalOpen(true);
  };

  const variance = summary?.variance ?? 0;
  const fullyInvoiced = hasInvoices && Math.abs(variance) < 0.01;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center text-base">
            <ReceiptText className="h-4 w-4 mr-2" aria-hidden="true" />
            Invoices
          </CardTitle>
          {canManage && (
            <div className="flex items-center gap-1.5">
              {!hasDeposit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5"
                  onClick={() => openCreate(true)}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Add deposit invoice
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 px-3 text-xs gap-1.5"
                onClick={() => openCreate(false)}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Add invoice
              </Button>
            </div>
          )}
        </div>

        {/* Totals-first reconciliation strip */}
        {summary && (summary.poTotal > 0 || hasInvoices) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground pt-1">
            {summary.poTotal > 0 && (
              <span>
                PO total{' '}
                <span className="font-medium tabular-nums text-foreground/80">
                  {format(summary.poTotal, currency)}
                </span>
              </span>
            )}
            {summary.poTotal > 0 && <span className="text-muted-foreground/40">·</span>}
            <span>
              Invoiced{' '}
              <span className="font-medium tabular-nums text-foreground/80">
                {format(summary.totalInvoiced, currency)}
              </span>
              {summary.invoiceCount > 0 && <span> ({summary.invoiceCount})</span>}
            </span>
            {summary.poTotal > 0 && (
              <>
                <span className="text-muted-foreground/40">·</span>
                {fullyInvoiced ? (
                  <span className="text-emerald-700">Fully invoiced</span>
                ) : variance > 0 ? (
                  <span>
                    Outstanding{' '}
                    <span className="font-medium tabular-nums text-foreground/80">
                      {format(variance, currency)}
                    </span>
                  </span>
                ) : hasInvoices ? (
                  <span className="text-amber-700">
                    Over by{' '}
                    <span className="font-medium tabular-nums">
                      {format(Math.abs(variance), currency)}
                    </span>
                  </span>
                ) : null}
              </>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {invoicesQuery.isLoading ? (
          <div className="space-y-1.5">
            {[0, 1].map((index) => (
              <div key={index} className="rounded-md border p-3 space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-3 w-36" />
              </div>
            ))}
          </div>
        ) : !hasInvoices && !hasLegacy ? (
          <div className="py-6 text-center">
            <FileText className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">No invoices yet</p>
            <p className="mt-0.5 text-xs text-muted-foreground/70">
              Add one when the supplier sends it.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {invoices.map((invoice) => {
              const file = invoice.file_id ? fileById.get(invoice.file_id) : undefined;
              const isPushed = Boolean(invoice.xero_bill_id);
              const isPushing = pushingInvoiceId === invoice.id;
              const invoiceDate = formatDateOnly(invoice.invoice_date);
              const dueDate = formatDateOnly(invoice.due_date);
              return (
                <div
                  key={invoice.id}
                  className="flex items-start gap-2 rounded-md border p-3 hover:bg-muted/40"
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => openEdit(invoice)}
                    aria-label={`View invoice ${invoice.invoice_number}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium">
                          {invoice.invoice_number}
                        </span>
                        {invoice.is_deposit && (
                          <Badge
                            variant="outline"
                            className="shrink-0 px-1.5 text-[10px] font-semibold uppercase tracking-wide"
                          >
                            Deposit
                          </Badge>
                        )}
                      </span>
                      <span className="shrink-0 text-sm font-semibold tabular-nums">
                        {format(Number(invoice.total ?? 0), currency)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span className="truncate">
                        {invoiceDate}
                        {dueDate && (
                          <>
                            {invoiceDate && ' · '}due {dueDate}
                          </>
                        )}
                        {!invoiceDate && !dueDate && '-'}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {file && <Paperclip className="h-3 w-3" aria-hidden="true" />}
                        {isPushed ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            <Check className="h-3 w-3" aria-hidden="true" />
                            Xero
                          </span>
                        ) : (
                          <span className="text-muted-foreground/60">Not pushed</span>
                        )}
                      </span>
                    </div>
                  </button>

                  <div className="flex shrink-0 items-center gap-1">
                    {isPushed && invoice.xero_bill_id && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={`${XERO_BILL_URL}${invoice.xero_bill_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-emerald-700 hover:bg-emerald-50"
                              aria-label={`Open bill ${invoice.invoice_number} in Xero`}
                            >
                              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>Open in Xero</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label={`Actions for invoice ${invoice.invoice_number}`}
                          >
                            {isPushing ? (
                              <RefreshCw
                                className="h-3.5 w-3.5 animate-spin text-muted-foreground motion-reduce:animate-none"
                                aria-hidden="true"
                              />
                            ) : (
                              <MoreHorizontal
                                className="h-3.5 w-3.5 text-muted-foreground"
                                aria-hidden="true"
                              />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(invoice)}>
                            <Pencil className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                            View and edit
                          </DropdownMenuItem>
                          {canPushXero && (
                            <DropdownMenuItem
                              disabled={isPushing || invoice.items.length === 0}
                              onClick={() => {
                                setPushingInvoiceId(invoice.id);
                                pushInvoice.mutate({ invoiceId: invoice.id });
                              }}
                            >
                              <Link2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                              {isPushed ? 'Re-push to Xero' : 'Push to Xero'}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            disabled={isPushed}
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteTarget(invoice)}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                            {isPushed ? 'Delete (pushed to Xero)' : 'Delete'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Legacy PO-level invoice flow — read-mostly, retired once
                po_invoices exist. Push buttons stay functional. */}
            {hasLegacy && (
              <div className="rounded-md border border-dashed bg-muted/30 p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" />
                  <span className="truncate text-sm font-medium">
                    {legacyInvoiceNumber || 'Legacy invoice'}
                  </span>
                  <Badge
                    variant="outline"
                    className="shrink-0 border-amber-300/80 bg-amber-100 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                  >
                    Legacy
                  </Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    Added before multi-invoice support
                  </span>
                </div>

                {/* Invoice files */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">Invoice file</Label>
                    {canManage && (
                      <label>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) void handleLegacyFileUpload(file);
                            event.target.value = '';
                          }}
                          disabled={isUploadingLegacyFile}
                        />
                        <Button type="button" variant="ghost" size="sm" className="h-7 px-2" asChild>
                          <span>
                            {isUploadingLegacyFile ? (
                              <RefreshCw
                                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                                aria-hidden="true"
                              />
                            ) : (
                              <Upload className="h-4 w-4" aria-hidden="true" />
                            )}
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                  {invoiceFiles.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No invoice uploaded</p>
                  ) : (
                    <div className="space-y-1">
                      {invoiceFiles.map((row) => (
                        <div
                          key={row.id}
                          className="flex items-center gap-2 rounded bg-muted px-2 py-1.5 text-sm"
                        >
                          <File className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                          <span className="flex-1 truncate" title={row.file_name}>
                            {row.file_name}
                          </span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {formatBytes(row.file_size)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 shrink-0 p-0"
                            aria-label={`Download ${row.file_name}`}
                            onClick={() => void handleDownloadFile(row.id)}
                          >
                            <Download className="h-3 w-3" aria-hidden="true" />
                          </Button>
                          {canManage && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 shrink-0 p-0 text-red-500 hover:text-red-600"
                              aria-label={`Delete ${row.file_name}`}
                              onClick={() => {
                                if (window.confirm(`Delete file "${row.file_name}"?`)) {
                                  deleteFile.mutate({ fileId: row.id });
                                }
                              }}
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Legacy invoice amount */}
                <div>
                  <Label htmlFor="legacy-invoice-amount" className="text-sm text-muted-foreground">
                    Invoice amount
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      {currency === 'USD' ? '$' : currency === 'AUD' ? 'A$' : currency}
                    </span>
                    <Input
                      id="legacy-invoice-amount"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={invoiceAmountInput}
                      onChange={(event) => setInvoiceAmountInput(event.target.value)}
                      onBlur={handleInvoiceAmountBlur}
                      className="h-8 pl-8 text-sm"
                      disabled={!canManage}
                    />
                  </div>
                </div>

                {/* Legacy Xero pushes */}
                {canPushXero && (
                  <div className="space-y-3 border-t pt-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Xero bill</p>
                      <p className="text-sm font-medium">
                        {order.xero_invoice_id ? (
                          <a
                            href={`${XERO_BILL_URL}${order.xero_invoice_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
                          >
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            Connected
                            <ExternalLink className="h-3 w-3" aria-hidden="true" />
                          </a>
                        ) : (
                          'Not pushed'
                        )}
                      </p>
                    </div>
                    {canManage && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block w-full">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => pushLegacyBill.mutate({ orderId: order.id })}
                                disabled={pushLegacyBill.isPending || invoiceFiles.length === 0}
                              >
                                {pushLegacyBill.isPending ? (
                                  <RefreshCw
                                    className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
                                )}
                                {order.xero_invoice_id ? 'Update Xero bill' : 'Push to Xero'}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {invoiceFiles.length === 0 && (
                            <TooltipContent>Upload an invoice before pushing to Xero</TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {(order.deposit_amount ?? 0) > 0 && (
                      <>
                        <div>
                          <p className="text-sm text-muted-foreground">Xero deposit bill</p>
                          <p className="text-sm font-medium">
                            {order.deposit_xero_bill_id ? (
                              <a
                                href={`${XERO_BILL_URL}${order.deposit_xero_bill_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
                              >
                                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                                Connected
                                <ExternalLink className="h-3 w-3" aria-hidden="true" />
                              </a>
                            ) : (
                              'Not pushed'
                            )}
                          </p>
                        </div>
                        {canManage && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => pushLegacyDeposit.mutate({ orderId: order.id })}
                            disabled={pushLegacyDeposit.isPending}
                          >
                            {pushLegacyDeposit.isPending ? (
                              <RefreshCw
                                className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                                aria-hidden="true"
                              />
                            ) : (
                              <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
                            )}
                            {order.deposit_xero_bill_id ? 'Update Xero deposit' : 'Push deposit'}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Create / edit modal */}
      <InvoiceModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        orderId={order.id}
        poNumber={order.po_number}
        poItems={order.items ?? []}
        currency={currency}
        invoice={editingInvoice}
        isDeposit={editingInvoice ? editingInvoice.is_deposit : creatingDeposit}
        depositPercent={(order as { deposit_percent?: number | null }).deposit_percent ?? null}
        depositAmount={order.deposit_amount ?? null}
        paymentTermsDays={paymentTermsDays}
        onSaved={() => void invalidateInvoices()}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete invoice?</DialogTitle>
            <DialogDescription>
              Invoice <strong>{deleteTarget?.invoice_number}</strong> and its line items will be
              permanently removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleteTarget && deleteInvoice.mutate({ invoiceId: deleteTarget.id })}
              disabled={deleteInvoice.isPending}
            >
              {deleteInvoice.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
